/**
 * §14's charge fields — the article PICKER and the charge preview box.
 *
 * Authority: `06-hearings.md` §14 (the three charge selects, their optgroups
 * `Level One — Article 2.2` / `Level Two — Article 2.3` / `Level Three — Article 2.4`, the
 * option text `2.2.a — {first 74 chars}…`, the three preview defaults and the preview line
 * `Article 2.3.a · Level 2` + the quoted wording + `**Minimum penalty:** {LEVEL_MIN}`), §7
 * (the ladder and the seriousness grading), §2 (the catalogue is bound to a Code edition and
 * is never a hard-coded list).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A SEPARATE FILE, AND WHY
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `CreateHearingModal.tsx` imports this and swaps its charge fieldset for it, and that is the
 * whole of the change to that file. The modal's other fields — the phase control, the match
 * reference, the two instants — are untouched, because two builds are in flight over that one
 * component and a picker written INTO it would have been a merge conflict across a control
 * neither change is about.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO MODES, AND THE FALLBACK IS NOT A DEGRADED ONE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * **Picked.** When the tournament has bound a Code edition AND a catalogue has been loaded
 * against it, each charge is §14's select: three optgroups, the articles of the bound edition,
 * and a preview box showing the wording, the level and the minimum penalty. Choosing an
 * article fills the article code, the level and the wording together, because in the Code they
 * ARE together — a charge is one row of one edition, not three fields that happen to agree.
 *
 * **Typed.** When there is no catalogue — no edition bound, or an edition bound and nothing
 * loaded — the three fields are asked for by hand, exactly as they were before this component
 * existed, and the reason is printed on the control with a link to where the catalogue is
 * loaded. This is not a fallback in the sense of a lesser experience; it is the only honest
 * one. §2 forbids a hard-coded list, so there is no built-in catalogue to fall back TO, and a
 * disabled select with the word "unavailable" beside it would stop a Technical Delegate from
 * creating a case at all — which is worse than typing, on a tournament where a case may need
 * to be opened before Configuration §05 has been finished.
 *
 * The wording travels either way. `charge.article_text` is quoted into the Hearing Letter, the
 * minutes and the decision letter, and this component sends it on both paths for that reason —
 * picking is a convenience over typing, not a different data model.
 */

import { useState } from "react";

import {
  HEARING_LEVEL_MINIMUM_PENALTY,
  codeOfConductOptionLabel,
  type HearingLevel,
} from "@hockeytd/shared";

import { fetchCodeOfConduct } from "../../api/client.js";
import type {
  CodeOfConductBody,
  CodeOfConductLevelGroup,
  HearingSeriousness,
} from "../../api/types.js";
import { useAsync } from "../../app/useAsync.js";

/** One charge as the modal holds it while the form is open. */
export interface ChargeDraft {
  article: string;
  level: HearingLevel;
  articleText: string;
  seriousness: HearingSeriousness;
}

export function emptyCharge(): ChargeDraft {
  return { article: "", level: 1, articleText: "", seriousness: "Low" };
}

const LEVELS: readonly HearingLevel[] = [1, 2, 3];
const SERIOUSNESS: readonly HearingSeriousness[] = ["Low", "Medium", "High"];

/** §14's three preview defaults, verbatim. */
export const CHARGE_PREVIEW_DEFAULTS = [
  "Pick an article to see its wording, level and minimum penalty.",
  "No second charge.",
  "No third charge.",
] as const;

/** §14's placeholder option, per charge. Charge 1 is required; 2 and 3 are not. */
function placeholderFor(index: number): string {
  return index === 0 ? "Select the article breached" : "No further charge";
}

export interface ChargePickerProps {
  charges: readonly ChargeDraft[];
  onChange: (index: number, patch: Partial<ChargeDraft>) => void;
}

export function ChargePicker({ charges, onChange }: ChargePickerProps): React.JSX.Element {
  const catalogue = useAsync<CodeOfConductBody>(() => fetchCodeOfConduct(), []);
  /**
   * Lets the TD go back to typing on a tournament that HAS a catalogue.
   *
   * It is here because a catalogue is transcribed by a person from a PDF, and the case that
   * needs an article the transcription missed is exactly the case nobody planned for. Refusing
   * to open it until Configuration is corrected would put a natural-justice clock behind a
   * data-entry task.
   */
  const [typing, setTyping] = useState(false);

  const loaded = catalogue.status === "ready" ? catalogue.data : null;
  const groups: CodeOfConductLevelGroup[] = loaded?.levels ?? [];
  const usable = loaded !== null && loaded.instrument !== null && loaded.articleCount > 0;
  const picking = usable && !typing;

  /** The minimum penalty for a level — the tournament's ladder where it has one (§7). */
  function minimumPenalty(level: HearingLevel): string {
    return (
      groups.find((group) => group.level === level)?.minimumPenalty ??
      HEARING_LEVEL_MINIMUM_PENALTY[level]
    );
  }

  /** Choosing an article sets all three fields at once, because the Code holds them as one. */
  function pick(index: number, article: string): void {
    if (article === "") {
      onChange(index, { article: "", articleText: "" });
      return;
    }
    for (const group of groups) {
      const found = group.articles.find((row) => row.article === article);
      if (found !== undefined) {
        onChange(index, {
          article: found.article,
          level: found.level,
          articleText: found.articleText,
        });
        return;
      }
    }
  }

  return (
    <fieldset className="hmodal__group" data-testid="chargePicker">
      <legend className="field__label">Charges</legend>

      {catalogue.status === "loading" ? (
        <p className="field__sub" data-testid="chargeCatalogueLoading">
          Loading the Code of Conduct catalogue bound to this tournament.
        </p>
      ) : null}

      {loaded !== null && loaded.instrument === null ? (
        <p className="field__sub" data-testid="chargeNoInstrument">
          <b>No Code of Conduct edition is bound to this tournament</b>, so there is no
          catalogue to pick from. The offence catalogue is keyed to a specific Code version
          because the article lettering shifts between editions, so it is not offered against a
          default. Bind the edition under Tournament Configuration, load its articles, and this
          field becomes a list. Until then the article, its level and its wording are quoted in
          by hand from the Code bound to this tournament.
        </p>
      ) : null}

      {loaded !== null && loaded.instrument !== null && loaded.articleCount === 0 ? (
        <p className="field__sub" data-testid="chargeEmptyCatalogue">
          <b>
            {loaded.instrument.title}, {loaded.instrument.versionLabel}
          </b>{" "}
          is bound to this tournament, but no articles have been loaded against it yet. Load the
          catalogue under Tournament Configuration and this field becomes a list. Until then the
          article, its level and its wording are quoted in by hand.
        </p>
      ) : null}

      {catalogue.status === "error" ? (
        <p className="field__sub" data-testid="chargeCatalogueError">
          <b>The Code of Conduct catalogue could not be read.</b> {catalogue.error.message} The
          article, its level and its wording are quoted in by hand instead — the case is not
          held up by it.
        </p>
      ) : null}

      {usable ? (
        <p className="field__sub" data-testid="chargeCatalogueSource">
          Articles quoted from <b>{loaded.instrument?.citationString}</b> —{" "}
          {loaded.articleCount} articles loaded.{" "}
          <button
            type="button"
            className="gbtn gbtn--sm"
            onClick={() => {
              setTyping((now) => !now);
            }}
          >
            {picking ? "Quote An Article By Hand" : "Pick From The Catalogue"}
          </button>
          {picking
            ? ""
            : " The wording typed here is what the letter, the minutes and the decision quote."}
        </p>
      ) : null}

      {charges.map((charge, index) => (
        <div className="hmodal__charge" key={index}>
          {picking ? (
            <div className="formgrid">
              <label className="field">
                <span className="field__sub">
                  Charge {index + 1} {index === 0 ? "· Required" : "· Optional"}
                </span>
                <select
                  aria-label={`Charge ${index + 1} article`}
                  value={charge.article}
                  onChange={(event) => {
                    pick(index, event.target.value);
                  }}
                >
                  <option value="">{placeholderFor(index)}</option>
                  {groups.map((group) => (
                    <optgroup key={group.level} label={group.label}>
                      {group.articles.map((row) => (
                        <option key={row.id} value={row.article}>
                          {codeOfConductOptionLabel(row.article, row.articleText)}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </label>
              <label className="field">
                <span className="field__sub">Seriousness Within The Level</span>
                <select
                  aria-label={`Charge ${index + 1} seriousness`}
                  value={charge.seriousness}
                  onChange={(event) => {
                    onChange(index, { seriousness: event.target.value as HearingSeriousness });
                  }}
                >
                  {SERIOUSNESS.map((one) => (
                    <option key={one} value={one}>
                      {one}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          ) : (
            <>
              <div className="formgrid">
                <label className="field">
                  <span className="field__sub">
                    Charge {index + 1} article {index === 0 ? "· Required" : "· Optional"}
                  </span>
                  <input
                    aria-label={`Charge ${index + 1} article`}
                    value={charge.article}
                    autoComplete="off"
                    placeholder="For example 2.2.f"
                    onChange={(event) => {
                      onChange(index, { article: event.target.value });
                    }}
                  />
                </label>
                <label className="field">
                  <span className="field__sub">Level</span>
                  <select
                    aria-label={`Charge ${index + 1} level`}
                    value={charge.level}
                    onChange={(event) => {
                      onChange(index, {
                        level: Number.parseInt(event.target.value, 10) as HearingLevel,
                      });
                    }}
                  >
                    {LEVELS.map((level) => (
                      <option key={level} value={level}>
                        Level {level}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span className="field__sub">Seriousness Within The Level</span>
                  <select
                    aria-label={`Charge ${index + 1} seriousness`}
                    value={charge.seriousness}
                    onChange={(event) => {
                      onChange(index, { seriousness: event.target.value as HearingSeriousness });
                    }}
                  >
                    {SERIOUSNESS.map((one) => (
                      <option key={one} value={one}>
                        {one}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <label className="field">
                <span className="field__sub">Article wording, quoted from the bound Code</span>
                <textarea
                  aria-label={`Charge ${index + 1} wording`}
                  value={charge.articleText}
                  onChange={(event) => {
                    onChange(index, { articleText: event.target.value });
                  }}
                />
              </label>
            </>
          )}

          {/*
           * §14's preview box. It shows what the charge WILL say — the article, its level, the
           * wording that is carried into three served documents, and the minimum penalty the
           * level carries — because a charge picked from a list is otherwise a code with no
           * consequence visible beside it.
           */}
          <div className="field__sub" data-testid={`chargePreview${index + 1}`}>
            {charge.article.trim() === "" || charge.articleText.trim() === "" ? (
              CHARGE_PREVIEW_DEFAULTS[index] ?? CHARGE_PREVIEW_DEFAULTS[2]
            ) : (
              <>
                <b>
                  Article {charge.article} · Level {charge.level}
                </b>
                <br />
                {charge.articleText}
                <br />
                <b>Minimum penalty:</b> {minimumPenalty(charge.level)}
              </>
            )}
          </div>
        </div>
      ))}
    </fieldset>
  );
}
